import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { activites } from "../mock";

const ActiviteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const activite = activites.find((a) => a.id === id);

  if (!activite) {
    return (
      <section className="bg-[#F7F4EF] py-32 text-center" data-nav-theme="light">
        <p className="text-neutral-600">Cette activité n'existe pas.</p>
        <Link to="/" className="mt-4 inline-block underline">
          Retour à l'accueil
        </Link>
      </section>
    );
  }

  const others = activites.filter((a) => a.id !== id);

  return (
    <>
      <section className="relative pt-40 md:pt-48 pb-20 md:pb-28 bg-[#F7F4EF]" data-nav-theme="light">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-neutral-500 text-[12px] uppercase tracking-widest mb-10 hover:opacity-70 transition-opacity"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Notre activité
          </button>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-6">
              <div className="flex items-center gap-3 text-neutral-400 text-[11px] uppercase tracking-[0.3em] mb-5">
                <span className="w-8 h-px bg-current opacity-60" />
                <span>{activite.num} / {String(activites.length).padStart(2, "0")}</span>
              </div>
              <h1
                className="font-display uppercase text-neutral-900 leading-[0.95]"
                style={{ fontSize: "clamp(38px, 5.5vw, 78px)" }}
              >
                {activite.title}
              </h1>
              <p className="text-neutral-500 italic mt-4">{activite.subtitle}</p>
              <p className="text-neutral-700 mt-7 text-[16px] leading-relaxed max-w-lg">
                {activite.text}
              </p>

              <button
                onClick={() => navigate("/contact")}
                className="mt-10 inline-flex items-center gap-3 bg-neutral-900 text-white rounded-full pl-6 pr-2 py-2 text-[12px] uppercase tracking-widest font-medium hover:bg-[#9F8B76] transition-colors group active:scale-95"
              >
                {activite.ctaLabel || "Discuter de mon projet"}
                <span className="w-9 h-9 rounded-full bg-[#9F8B76] flex items-center justify-center">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
            </div>

            <div className="md:col-span-6">
              <div className="relative aspect-[4/5] md:aspect-[5/6] w-full max-w-[560px] ml-auto overflow-hidden">
                <img
                  src={activite.image}
                  alt={activite.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28" data-nav-theme="light">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-7 space-y-5 text-[15px] md:text-[16px] leading-relaxed text-neutral-700">
              {activite.details?.map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
            {activite.highlights && (
              <div className="md:col-span-5">
                <span className="uppercase tracking-[0.25em] text-[11px] text-neutral-400 mb-5 block">
                  Ce que couvre cette prestation
                </span>
                <ul className="space-y-3">
                  {activite.highlights.map((h, i) => (
                    <li key={i} className="flex gap-3 text-[14px] text-neutral-700 border-t border-neutral-200 pt-3">
                      <span className="text-[#9F8B76] shrink-0">—</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F4EF] py-20 md:py-28" data-nav-theme="light">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="h-px w-10 bg-[#9F8B76]" />
            <span className="uppercase tracking-[0.3em] text-[11px] text-neutral-500">
              Nos autres activités
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((a) => (
              <button
                key={a.id}
                onClick={() => navigate(`/activite/${a.id}`)}
                className="text-left p-7 bg-white border border-neutral-200 hover:border-[#9F8B76] transition-colors group"
              >
                <span className="text-[11px] uppercase tracking-[0.25em] text-neutral-400">
                  {a.num} / {String(activites.length).padStart(2, "0")}
                </span>
                <h3 className="font-display uppercase text-neutral-900 text-xl mt-2 mb-2 group-hover:text-[#9F8B76] transition-colors">
                  {a.title}
                </h3>
                <p className="text-neutral-600 text-[13px] leading-relaxed">{a.text}</p>
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ActiviteDetail;
